import React, { useEffect, useState } from 'react';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  isProcessing?: boolean;
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isProcessing = false,
  destructive = false,
  onCancel,
  onConfirm,
}) => {
  const [isRendered, setIsRendered] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  const animationDurationMs = 200;

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      const showTimer = window.setTimeout(() => setIsVisible(true), 10);
      return () => window.clearTimeout(showTimer);
    }

    setIsVisible(false);
    const hideTimer = window.setTimeout(() => setIsRendered(false), animationDurationMs);
    return () => window.clearTimeout(hideTimer);
  }, [isOpen]);

  useEffect(() => {
    if (!isRendered) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isProcessing) {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isRendered, isProcessing, onCancel]);

  if (!isRendered) {
    return null;
  }

  const confirmClassName = destructive
    ? 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500'
    : 'bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-ring';

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center px-4 transition-[opacity,background-color,backdrop-filter] duration-200 ease-out ${isVisible ? 'bg-black/50 opacity-100 backdrop-blur-sm' : 'bg-black/0 opacity-0 backdrop-blur-none'}`}
      role="presentation"
      onClick={() => {
        if (!isProcessing) {
          onCancel();
        }
      }}
    >
      <div
        className={`w-full max-w-sm rounded-xl border border-border bg-card p-5 text-card-foreground shadow-2xl transition-[opacity,transform] duration-200 ease-out ${isVisible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-3 scale-95 opacity-0'}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-message"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="confirmation-dialog-title" className="text-lg font-semibold text-foreground">
          {title}
        </h3>
        <p id="confirmation-dialog-message" className="mt-2 text-sm leading-6 text-muted-foreground">
          {message}
        </p>

        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isProcessing}
            className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${confirmClassName}`}
          >
            {isProcessing ? 'Working...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
